import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { Pagination, LetterPagination, SortDropdown } from '@tovia/man-ui';
import styled from 'styled-components/macro';
import { FormattedMessage } from 'react-intl';
import PaginationModal from '../../components/modals/PaginationModal';

import { load } from '../../redux/modules/photographers';
import { SORT_TYPES } from 'src/shared/constants/photographers';
import { setTitle } from '../../redux/modules/app';
import PhotographerCards from '../Cards/Photographer/PhotographerCards';
import ConnectedViewOptions from '../ConnectedViewOptions';
import { useSelector, useSettingsSelector } from 'src/client/redux';
import Helmet from '../Helmet/Helmet';
import urls from 'src/shared/urls';
import { isPrerender } from '../../utils/prerenderDetect';
import { ModelsPlaceholder } from 'src/client/components/Placeholders/Layouts/ModelsPlaceholder';
import { useWidePageContext } from '../App/WidePageContextProvider';

const sortTypesTitles = {
  top: 'Top',
  all: 'All',
};

const Div = styled.div`
  .options {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 10px 0;
  }
`;

export default function Photographers() {
  const params = useParams<{ page: string; sortBy: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const widePage = useWidePageContext();

  const site = useSelector((state) => state.site);
  const { error, loaded, pageSize, photographers, firstNameLetter, total } = useSelector(
    (state) => state.photographers,
  );
  const nameLetters = useSelector((state) => state.photographers.nameLetters.items);
  const page = Number(params.page);

  useEffect(() => {
    if ((loaded && photographers.length === 0) || error?.status === 404) {
      history.replace(urls.get.notFound);
    }
  }, [error, history, loaded, photographers]);

  const nameLettersMap = useMemo(
    () => (Object.keys(nameLetters).length > 0 ? nameLetters.map((letter) => letter.firstNameLetter) : []),
    [nameLetters],
  );
  const sortBy = useMemo(() => params.sortBy || SORT_TYPES[0].id, [params.sortBy]);

  const photographerViewType = useSettingsSelector('listView');

  useEffect(() => {
    const sortByVal = params.sortBy || SORT_TYPES[0].id;
    dispatch(setTitle(`${sortTypesTitles[sortByVal]} Photographers`));
  }, [params.sortBy, dispatch]);

  const setPage = useCallback(
    (_page) => {
      history.push(`/photographers/${sortBy}/${_page}`);
    },
    [history, sortBy],
  );

  useEffect(() => {
    if (sortBy) {
      // pass state.letter into call when supported by API
      const loadParams: {
        sortBy: string;
        page: number;
        firstNameLetter?: string;
        first?: number;
      } = {
        sortBy,
        page: Number(page) || 1,
      };

      if (sortBy === 'all') {
        loadParams.firstNameLetter = params.page || '';
      } else {
        loadParams.first = pageSize;
      }

      dispatch(load(loadParams));
    }
  }, [dispatch, page, pageSize, sortBy, params.page]);

  const getSortUrl = useCallback((value) => `/photographers/${value}`, []);

  const changeSort = useCallback(
    (value) => {
      history.push(getSortUrl(value));
    },
    [getSortUrl, history],
  );

  const renderLetterIndex = useCallback(
    (_firstNameLetter) => {
      if (sortBy === 'all') {
        return (
          <LetterPagination
            activeLetter={_firstNameLetter}
            onChange={(letter) => setPage(letter)}
            pageUrlPrefix={`/photographers/${sortBy}`}
            letters={nameLettersMap}
          />
        );
      }

      return null;
    },
    [sortBy, nameLettersMap, setPage],
  );

  const renderPhotographersContent = useCallback(
    () => (
      <Div>
        {isPrerender && (
          <div>
            Sort By:{' '}
            <div>
              {SORT_TYPES.map((sortType) => {
                const link = getSortUrl(sortType.id);
                return (
                  <Link key={sortType.id} to={link}>
                    {' '}
                    <FormattedMessage id={sortType.intlID} defaultMessage={sortType.title} />
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        {!isPrerender && !widePage && (
          <div className="options">
            <SortDropdown menuOptions={SORT_TYPES} value={sortBy} onChange={changeSort} />
            <ConnectedViewOptions />
          </div>
        )}
        {renderLetterIndex(firstNameLetter)}

        <PhotographerCards photographers={photographers} />
        {renderLetterIndex(firstNameLetter)}
        {sortBy !== 'all' && (
          <Pagination
            activePage={page || 1}
            boundaryRange={1}
            onPageChange={setPage}
            pageUrlPrefix={`/photographers/${sortBy}`}
            siblingRange={1}
            totalPages={Math.ceil(total / pageSize)}
            showPrevNext
            modal={
              <PaginationModal setPage={(pageNumber) => setPage(pageNumber)} totalPages={Math.ceil(total / pageSize)} />
            }
          />
        )}
      </Div>
    ),
    [
      getSortUrl,
      changeSort,
      photographers,
      sortBy,
      renderLetterIndex,
      total,
      pageSize,
      setPage,
      page,
      firstNameLetter,
      widePage,
    ],
  );

  useEffect(() => {
    if (!isPrerender) {
      window.dataLayer.push({
        event: 'select_content',
        content_type: 'photographers',
        page: params.page || 1,
      });
    }
  }, [params.page]);

  const errorMarkup = error ? null : null; // Todo Create Content Error Container
  const photographerContentMarkup = loaded ? (
    renderPhotographersContent()
  ) : (
    <ModelsPlaceholder detailed={photographerViewType === 'detailed'} />
  );
  return (
    <div>
      <Helmet
        id="photographers"
        canonicalUrl={`${window.location.origin}/photographers`}
        defaultTitle="Photographers Index {{^firstPage}}(Page {{page}}){{/firstPage}} - {{domainName}}"
        variables={{
          page,
          domainName: site.domain,
          firstPage: !page || page === 1,
          siteName: site.name,
        }}
      >
        <meta property="prerender:ttl" content="86400" />
      </Helmet>
      {errorMarkup}
      {photographerContentMarkup}
    </div>
  );
}
